import logoPecaAi from '@/assets/images/logo-peca-ai.svg';
import { Alert, Button, Col, Divider, Empty, Image, Modal, Row } from 'antd';
import PecaAiAddProductForm from './AddProductForm';
import { Group, SaveButton } from '@/components/_common';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import PecaAiListProduct from './ListProduct';
import {
  useWorkOrderComplementBindWithPecaAi,
  useWorkOrderComplementCreatePecaAiOrder,
  useWorkorderComplementSyncWithPecaAi,
  WORKORDER_COMPLEMENT_DETAIL_KEY,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.hooks';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import { usePecaAiContext } from '../PecaAiProvider';
import { useCallback, useMemo } from 'react';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import { WORKORDER_ITEMS_QUERY_KEY } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.hooks';
import { WorkOrderComplementStatus } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { applyCpfCnpjMask } from '@/utils/mask';

interface Props {
  opened: boolean;
  onClose: VoidFunction;
  hasBasket: boolean;
  isLoading: boolean;
}

export default function PecaAiBasketModal(props: Props) {
  const {
    workorder: { data: workorder },
    complement: { data: complement },
    company: { data: company },
  } = useWorkorderDetailContext();

  const hasVehicle = !!workorder?.vehicle?.licensePlate;

  const { products } = usePecaAiContext();

  const hasProducts = useMemo(() => !!products.length, [products]);

  const hasProductsInBasket = useMemo(
    () => !!products.filter((product) => product.inBasket).length,
    [products],
  );

  const bindBasket = useWorkOrderComplementBindWithPecaAi();
  const syncBasket = useWorkorderComplementSyncWithPecaAi();
  const createPecaAiOrder = useWorkOrderComplementCreatePecaAiOrder();

  const isComplementAuthorizedOrClosed =
    complement?.status === WorkOrderComplementStatus.Authorized ||
    complement?.status === WorkOrderComplementStatus.Closed;

  const isLoading =
    bindBasket.isLoading || syncBasket.isLoading || createPecaAiOrder.isLoading;

  const queryChanel = useQueryChanel();

  const handleClose = () => {
    props.onClose();
  };

  const onBasketCreate = useCallback(async () => {
    try {
      if (!workorder || !complement) return;

      await bindBasket.mutateAsync({
        workOrderId: workorder.id,
        complementId: complement.id,
        description: `OS: ${complement.complementIdentifier} - ${workorder.vehicle.licensePlate}`,
        products: products.map((product) => ({
          amount: product.amount,
          productId: product.productId,
        })),
      });

      await syncBasket.mutateAsync({
        complementId: complement.id,
        workOrderId: workorder.id,
      });

      handleClose();

      queryChanel({
        resetQueries: [
          [WORKORDER_ITEMS_QUERY_KEY],
          [WORKORDER_COMPLEMENT_DETAIL_KEY],
        ],
      });
    } finally {
    }
  }, [workorder, complement, products]);

  const modalFooter = (
    <Row gutter={[16, 16]} justify="end">
      <Col>
        <Button onClick={handleClose} disabled={isLoading}>
          Fechar
        </Button>
      </Col>

      {!props.hasBasket && (
        <Col>
          <Button
            type="primary"
            onClick={onBasketCreate}
            loading={isLoading}
            disabled={!hasProducts}
          >
            Criar cesta
          </Button>
        </Col>
      )}

      {props.hasBasket && hasProductsInBasket && (
        <Col>
          <SaveButton
            loading={isLoading}
            disabled={!isComplementAuthorizedOrClosed}
            onClick={() =>
              createPecaAiOrder.mutateAsync({
                workOrderId: workorder?.id as number,
                complementId: complement?.id as number,
              })
            }
          >
            Gerar pedido
          </SaveButton>
        </Col>
      )}
    </Row>
  );

  return (
    <Modal
      title={
        <Group gap={10}>
          <Image src={logoPecaAi} preview={false} width={60} />
          {props.hasBasket ? (
            <span>Edição da cesta de produtos</span>
          ) : (
            <span>Criação da cesta de produtos</span>
          )}
        </Group>
      }
      open={props.opened}
      onCancel={handleClose}
      width="80%"
      style={{ position: 'relative', overflow: 'hidden', borderRadius: 8 }}
      confirmLoading={bindBasket.isLoading || syncBasket.isLoading}
      footer={hasVehicle ? modalFooter : false}
    >
      {props.isLoading && (
        <ModalLoadingOverlay posAbsolute messages={['Carregando dados...']} />
      )}

      {!hasVehicle && (
        <Empty description="Nenhum veículo foi selecionado no orçamento. Por favor, escolha um veículo para continuar." />
      )}

      {hasVehicle && (
        <>
          <PecaAiAddProductForm />

          <PecaAiListProduct hasBasket={props.hasBasket} />

          {props.hasBasket &&
            hasProductsInBasket &&
            !isComplementAuthorizedOrClosed && (
              <>
                <Divider />

                <Alert
                  type="warning"
                  message={<strong>Solicitação de compra</strong>}
                  description={
                    <>
                      <p>
                        Antes de gerar o pedido de compra, é necessário
                        solicitar a autorização do orçamento por parte do
                        cliente. Após a aprovação, o pedido será automaticamente
                        processado pelo sistema Peça Aí.
                      </p>
                      <p>
                        Se por algum motivo o pedido não for gerado
                        automáticamente, será habilitado o botão de gerar o
                        pedido de forma manual.
                      </p>
                    </>
                  }
                />
              </>
            )}

          {props.hasBasket && (
            <Alert
              type="info"
              style={{ marginTop: 16 }}
              message={<strong>Endereço de entrega</strong>}
              description={
                <>
                  <p>
                    Os produtos serão enviados para o endereço cadastrado no
                    PeçaAí relacionado ao CNPJ{' '}
                    <strong>{applyCpfCnpjMask(company?.cnpj)}</strong>
                  </p>
                </>
              }
            />
          )}
        </>
      )}
    </Modal>
  );
}
